import React, { useState, useEffect, useCallback } from 'react';
import { X, Euro, Calendar, Plus, Trash2, Loader2, AlertCircle, History } from 'lucide-react';
import { fetchTariffOverrides, createTariffOverride, deleteTariffOverride, type TariffOverride, type PriceType } from '../utils/tariffOverrides';
import { toast } from 'react-hot-toast';

interface TariffEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  participantId: string;
  participantName: string;
  participantType: string;
  entryDate: string;
  baselinePrices: {
    shared_energy_price: number | null;
    prosumer_buy_price: number | null;
    prosumer_sell_price: number | null;
  };
  onSaved: () => void;
}

const PRICE_LABELS: Record<PriceType, string> = {
  shared_energy_price: "Prix de l'énergie partagée",
  prosumer_buy_price: "Prix d'achat (consommation partagée)",
  prosumer_sell_price: "Prix de vente (injection partagée)",
};

export function TariffEditModal({
  isOpen,
  onClose,
  participantId,
  participantName,
  participantType,
  entryDate,
  baselinePrices,
  onSaved,
}: TariffEditModalProps) {
  const [overrides, setOverrides] = useState<TariffOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [newPrice, setNewPrice] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [selectedPriceType, setSelectedPriceType] = useState<PriceType>('shared_energy_price');
  const [formError, setFormError] = useState('');

  const availablePriceTypes: PriceType[] = participantType === 'prosumer'
    ? ['prosumer_buy_price', 'prosumer_sell_price']
    : ['shared_energy_price'];

  const loadOverrides = useCallback(async () => {
    if (!participantId) return;
    setLoading(true);
    try {
      const data = await fetchTariffOverrides(participantId);
      setOverrides(data);
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => {
    if (isOpen) {
      loadOverrides();
      setNewStartDate(entryDate || new Date().toISOString().split('T')[0]);
      setSelectedPriceType(availablePriceTypes[0]);
      setNewPrice('');
      setNewEndDate('');
      setFormError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!availablePriceTypes.includes(selectedPriceType)) {
      setSelectedPriceType(availablePriceTypes[0]);
    }
  }, [availablePriceTypes, selectedPriceType]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setFormError('');

    if (!newPrice.trim()) {
      setFormError('Le nouveau tarif est requis');
      return;
    }
    const priceVal = parseFloat(newPrice.replace(',', '.'));
    if (isNaN(priceVal) || priceVal < 0 || priceVal > 1000) {
      setFormError('Le tarif doit être un nombre entre 0 et 1000 EUR/MWh');
      return;
    }
    if (!newStartDate) {
      setFormError('La date de début est requise');
      return;
    }
    if (entryDate && newStartDate < entryDate) {
      setFormError(`La date de début ne peut pas être antérieure à la date d'entrée du membre (${entryDate})`);
      return;
    }
    if (newEndDate && newEndDate < newStartDate) {
      setFormError('La date de fin ne peut pas être antérieure à la date de début');
      return;
    }

    setSaving(true);
    try {
      await createTariffOverride(
        participantId,
        selectedPriceType,
        priceVal,
        newStartDate,
        newEndDate || null
      );
      toast.success('Tarif modifié avec succès');
      setNewPrice('');
      setNewEndDate('');
      await loadOverrides();
      onSaved();
    } catch (error: any) {
      setFormError(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (overrideId: string) => {
    if (!window.confirm('Supprimer cette modification de tarif ?')) return;
    setDeletingId(overrideId);
    try {
      await deleteTariffOverride(overrideId);
      toast.success('Modification supprimée');
      await loadOverrides();
      onSaved();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatPrice = (v: number | null | undefined) => (v != null ? `${v} EUR/MWh` : '—');

  const baselinePrice = baselinePrices[selectedPriceType];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Euro className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-neutral-900">Modifier le tarif</h2>
              <p className="text-sm text-neutral-500">{participantName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-6">
          {/* Baseline prices display */}
          <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">Tarif de base (à la création)</p>
            <div className="space-y-2">
              {availablePriceTypes.map((pt) => (
                <div key={pt} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-700">{PRICE_LABELS[pt]}</span>
                  <span className="font-semibold text-neutral-900">{formatPrice(baselinePrices[pt])}</span>
                </div>
              ))}
            </div>
          </div>

          {/* New override form */}
          <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <Plus className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-bold text-amber-900">Nouveau tarif</h3>
            </div>

            {availablePriceTypes.length > 1 && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-neutral-700 mb-1.5">Type de tarif</label>
                <select
                  value={selectedPriceType}
                  onChange={(e) => setSelectedPriceType(e.target.value as PriceType)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {availablePriceTypes.map((pt) => (
                    <option key={pt} value={pt}>{PRICE_LABELS[pt]}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1.5">
                  Nouveau tarif (EUR/MWh) *
                </label>
                <input
                  type="text"
                  value={newPrice}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                    setNewPrice(v);
                  }}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Ex: 95.00"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1.5">
                  Date de début *
                </label>
                <input
                  type="date"
                  value={newStartDate}
                  min={entryDate || undefined}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1.5">
                  Date de fin
                </label>
                <input
                  type="date"
                  value={newEndDate}
                  min={newStartDate || undefined}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="text-xs text-neutral-500 mt-1">Vide = jusqu'à nouvel ordre</p>
              </div>
            </div>

            <div className="mt-3 px-3 py-2 bg-white/60 rounded-lg border border-amber-100">
              <p className="text-xs text-neutral-600">
                {newEndDate
                  ? `Ce tarif s'appliquera du ${formatDate(newStartDate)} au ${formatDate(newEndDate)}, puis le tarif de base (${formatPrice(baselinePrice)}) reprendra.`
                  : `Ce tarif s'appliquera à partir du ${formatDate(newStartDate)} jusqu'à nouvel ordre.`
                }
              </p>
            </div>

            {formError && (
              <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={saving}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Ajouter le tarif
                </>
              )}
            </button>
          </div>

          {/* History */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-neutral-500" />
              <h3 className="text-sm font-bold text-neutral-900">Historique des modifications</h3>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
              </div>
            ) : overrides.length === 0 ? (
              <div className="text-center py-6 text-neutral-400 text-sm border border-dashed border-neutral-200 rounded-xl">
                Aucune modification de tarif enregistrée
              </div>
            ) : (
              <div className="space-y-2">
                {overrides.map((o) => (
                  <div key={o.id} className="flex items-center justify-between bg-white border border-neutral-200 rounded-lg px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium px-2 py-0.5 bg-neutral-100 rounded-full text-neutral-600">
                          {PRICE_LABELS[o.price_type]}
                        </span>
                        <span className="font-semibold text-neutral-900 text-sm">{o.new_price} EUR/MWh</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-neutral-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(o.start_date)} → {o.end_date ? formatDate(o.end_date) : 'Indéfini'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(o.id)}
                      disabled={deletingId === o.id}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                      title="Supprimer cette modification"
                    >
                      {deletingId === o.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
